import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, RotateCcw, ChevronDown } from 'lucide-react';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import { mapEditEntry, revertEdit, type EditEntry } from '../api/janitors';

interface EditLogProps {
  onClose: () => void;
}

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  categoryId: 'Position',
  tier: 'Level',
  type: 'Type',
  uniform: 'Gi/No-Gi',
  arts: 'Arts',
  author: 'Author',
  attribution: 'Credit',
  context: 'Context',
};

function fmt(v: unknown): string {
  if (v == null || v === '') return '—';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}

function kindLabel(e: EditEntry): string {
  if (e.revertOf) return 'Reverted';
  return e.kind === 'edit' ? 'Edited' : e.kind === 'archive' ? 'Archived' : 'Restored';
}

function when(d: Date | null): string {
  if (!d) return '…';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function whenShort(d: Date | null): string {
  if (!d) return '…';
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// One-line gist for the collapsed row: a single field shows its change inline;
// more than one just counts them.
function summary(e: EditEntry): string {
  if (e.revertOf) return 'Reverted';
  if (e.kind !== 'edit' || !e.after) return kindLabel(e);
  const keys = Object.keys(e.after);
  if (keys.length === 1) {
    const k = keys[0];
    return `${FIELD_LABELS[k] ?? k}: ${fmt(e.before?.[k])} → ${fmt(e.after[k])}`;
  }
  return `${keys.length} changes`;
}

// The last 100 library changes, newest first, with a Revert on each. Shared by
// the admin and every janitor so they can peer-review each other.
export default function EditLog({ onClose }: EditLogProps) {
  const [entries, setEntries] = useState<EditEntry[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [rowErr, setRowErr] = useState<Record<string, string>>({});
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'technique_edits'), orderBy('at', 'desc'), limit(100));
    return onSnapshot(q,
      (snap) => setEntries(snap.docs.map((d) => mapEditEntry(d.id, d.data()))),
      (e) => setLoadErr(e.message));
  }, []);

  const revert = async (e: EditEntry) => {
    if (busy) return;
    if (!confirm(`Revert ${kindLabel(e).toLowerCase()} on "${e.techniqueName ?? e.techniqueId}"?`)) return;
    setBusy(e.id);
    setRowErr((m) => ({ ...m, [e.id]: '' }));
    try {
      await revertEdit(e.id);
    } catch (err) {
      const msg = (err as { message?: string })?.message?.replace(/^.*?:\s*/, '') || 'Could not revert';
      setRowErr((m) => ({ ...m, [e.id]: msg }));
    } finally {
      setBusy(null);
    }
  };

  // Over the phone frame, not the whole viewport — same as the technique
  // details modal — so it's reachable on a phone.
  return createPortal(
    <div className="frame-modal-backdrop" onClick={onClose}>
      <div className="gift-modal" onClick={(e) => e.stopPropagation()}>
        <button className="gift-modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        <div className="code-table-head" style={{ marginBottom: 12 }}>
          <span>Edit Log</span>
          {entries && <span className="code-table-count">{entries.length}</span>}
        </div>
        {loadErr ? (
          <span className="gift-modal-error">{loadErr}</span>
        ) : !entries ? (
          <span className="code-table-empty">Loading…</span>
        ) : entries.length === 0 ? (
          <span className="code-table-empty">No changes yet.</span>
        ) : (
          <div className="edit-log-list">
            {entries.map((e) => {
              const isOpen = open === e.id;
              return (
                <div key={e.id} className={`edit-log-row${e.revertedBy ? ' edit-log-row--reverted' : ''}${isOpen ? ' edit-log-row--open' : ''}`}>
                  <button type="button" className="edit-log-line" onClick={() => setOpen(isOpen ? null : e.id)} aria-expanded={isOpen}>
                    <span className="edit-log-tech">{e.techniqueName ?? e.techniqueId}</span>
                    <span className={`edit-log-summary edit-log-summary--${e.revertOf ? 'revert' : e.kind}`}>{summary(e)}</span>
                    <span className="edit-log-when">{whenShort(e.at)}</span>
                    <ChevronDown size={14} className="edit-log-chevron" />
                  </button>
                  {isOpen && (
                    <div className="edit-log-detail">
                      <div className="edit-log-meta">
                        <span className="edit-log-by">{e.byName}</span>
                        <span>{kindLabel(e).toLowerCase()} · {when(e.at)}</span>
                      </div>
                      {e.kind === 'edit' && e.after && (
                        <ul className="edit-log-diff">
                          {Object.keys(e.after).map((k) => (
                            <li key={k}>
                              <span className="edit-log-field">{FIELD_LABELS[k] ?? k}</span>
                              <span className="edit-log-old">{fmt(e.before?.[k])}</span>
                              <span className="edit-log-arrow">→</span>
                              <span className="edit-log-new">{fmt(e.after?.[k])}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="edit-log-actions">
                        {e.revertedBy ? (
                          <span className="edit-log-reverted">Reverted by {e.revertedByName ?? e.revertedBy}</span>
                        ) : (
                          <button
                            type="button"
                            className="modal-btn modal-btn--cancel edit-log-revert"
                            onClick={() => revert(e)}
                            disabled={busy !== null}
                            title="Undo this change"
                          >
                            <RotateCcw size={13} />
                            {busy === e.id ? 'Reverting…' : 'Revert'}
                          </button>
                        )}
                        {rowErr[e.id] && <span className="gift-modal-error" style={{ margin: 0 }}>{rowErr[e.id]}</span>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>,
    document.querySelector('.phone-frame') ?? document.body,
  );
}
